import React from 'react';
import {
  ActionIcon,
  Card, DetailStack, Grid, ICON_CLOSE, ICON_COMPARE_OUTLINE, ICON_PLUS,
  Text
  ,
} from '@doveit/bricks';
import { formatDate } from '@doveit/hammer';
import { Contact } from '../../../providers/api/dtos';
import { removeNullAndUndefinedValues } from '../../../utils/array';
import { normalizeAiContactDataToContact } from '../../utils/mappers';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { highlightContactField } from '../../utils/highlightContactField';
import { ContactSuggestion } from '../../types';

export interface SuggestedContactCardProps extends React.AriaAttributes {
  suggestedContact: ContactSuggestion,
  onAdd?: (addedContact: Contact) => Promise<void> | void,
  onCompare?: (suggestedContact: Contact, existingContact: Contact) => Promise<void> | void,
  onIgnore?: (ignoredContact: Contact) => Promise<void> | void,
}

const SuggestedContactCard: React.FC<SuggestedContactCardProps> = ({
  suggestedContact,
  onAdd,
  onCompare,
  onIgnore,
  ...rest
}) => {
  const normalizedSuggestion = normalizeAiContactDataToContact(suggestedContact.suggestion);
  return (
    <Card aria-label="Card di un contatto suggerito" {...rest}>
      <Card.Header
        primaryActions={removeNullAndUndefinedValues([
          !suggestedContact.contact && (
            <ActionIcon
              icon={{ path: ICON_PLUS }}
              label="Aggiungi"
              aria-label="Aggiungi contatto suggerito"
              onClick={() => onAdd?.(normalizedSuggestion)}
              size="S"
            />
          ),
          suggestedContact.contact && (
            <ActionIcon
              icon={{ path: ICON_COMPARE_OUTLINE }}
              label="Compara/Aggiorna"
              aria-label="Compara e aggiorna contatto suggerito"
              onClick={() => onCompare?.(normalizedSuggestion, suggestedContact.contact!)}
              size="S"
            />
          ),
          <ActionIcon
            label="Ignora"
            aria-label="Ignora contatto suggerito"
            onClick={() => onIgnore?.(suggestedContact.suggestion)}
            icon={{ path: ICON_CLOSE }}
            size="S"
          />,
        ])}
      >
        <Text.H4 color={highlightContactField(suggestedContact, 'name')}>
          {suggestedContact.suggestion.name || 'Contatto'}
        </Text.H4>
      </Card.Header>
      <Card.Content>
        <Grid gutter={150}>
          <Grid.Unit size={{ XS: 1 / 2, SM: 1 / 4 }}>
            <DetailStack
              label="Data di nascita"
              color={highlightContactField(suggestedContact, 'birthday')}
            >
              {normalizedSuggestion.birthday ? formatDate(new Date(normalizedSuggestion.birthday)) : NO_VALUE_SYMBOL}
            </DetailStack>
          </Grid.Unit>

          <Grid.Unit size={{ XS: 1 / 2, SM: 1 / 4 }}>
            <DetailStack
              label="Luogo di nascita"
              color={highlightContactField(suggestedContact, 'birthplace')}
            >
              {normalizedSuggestion.birthplace || NO_VALUE_SYMBOL}
            </DetailStack>
          </Grid.Unit>

          <Grid.Unit size={{ XS: 1 / 2, SM: 1 / 4 }}>
            <DetailStack
              label="Codice fiscale"
              color={highlightContactField(suggestedContact, 'fiscalCode')}
            >
              {normalizedSuggestion.fiscalCode || NO_VALUE_SYMBOL}
            </DetailStack>
          </Grid.Unit>

          <Grid.Unit size={{ XS: 1 / 2, SM: 1 / 4 }}>
            <DetailStack
              label="Residenza"
              color={highlightContactField(suggestedContact, 'residence.normalizedAddress')}
            >
              {normalizedSuggestion.residence?.normalizedAddress || NO_VALUE_SYMBOL}
            </DetailStack>
          </Grid.Unit>
        </Grid>
      </Card.Content>
    </Card>
  );
};

export default SuggestedContactCard;
